import React, { FC, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';

import WebhookEventTable from './WebhookEventTable';
import FiltersPanel from './FiltersPanel';
import { FiltersButton } from '../../Paylinks/components';
import { RefreshButton } from '../../Paylinks/components/RefreshButton';
import { useWebhooksService } from '../../../hooks';
import { WebhookEndpoint } from '../types/types';

/** This allows dayjs to work with UTC */
dayjs.extend(utc);

type EventLogsTabProps = {
  /** MerchantId */
  merchantId: string;
  /** Active webhook subscriptions for retry diff detection */
  webhooks: WebhookEndpoint[];
};

/** This component displays the page rendered when the user clicks on the Event Logs tab */
const EventLogsTab: FC<EventLogsTabProps> = ({ merchantId, webhooks }) => {
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);

  const {
    events,
    eventsPageData: pageData,
    eventTypes,
    eventsLastTableLoadDate: lastTableLoadDate,
    eventsSortQuery: sortQuery,
    eventsSelectedFilters: selectedFilters,
    isEventsLoading: isLoading,
    isEventsDataLoading: isDataLoading,
    setEventsPageNumber,
    setEventsPageSize,
    setEventsSortQuery,
    applyEventsFilters,
    refetchWebhookEvents,
    retryWebhookEvent,
  } = useWebhooksService({ merchantId, skipFetch: true });

  const selectedFiltersSize =
    selectedFilters.selectedStatusFilters.size +
    selectedFilters.selectedEventFilters.size +
    (selectedFilters.selectedInitialSendDateTo ? 1 : 0) +
    (selectedFilters.selectedInitialSendDateFrom ? 1 : 0) +
    (selectedFilters.selectedLastAttemptDateTo ? 1 : 0) +
    (selectedFilters.selectedLastAttemptDateFrom ? 1 : 0);

  /** Handles filter button click */
  const onFilterClick = () => {
    setFiltersPanelOpen(true);
  };

  /** Handles filter panel close */
  const onFilterClose = () => {
    setFiltersPanelOpen(false);
  };

  const handleRefresh = () => {
    refetchWebhookEvents();
  };

  return (
    <Box sx={{ pt: 3, width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 4,
          gap: 2,
        }}
      >
        <Box>
          <Stack direction="row" gap={2} sx={{ alignItems: 'center' }}>
            <Typography variant="large" fontWeight="bold">
              Event Logs
            </Typography>
            <RefreshButton onClick={handleRefresh} disabled={isLoading || isDataLoading} />
          </Stack>
          {lastTableLoadDate && (
            <Typography variant="small" color="text.secondary">
              Data last loaded on {dayjs.utc(lastTableLoadDate).format('MM/DD/YY')} at{' '}
              {dayjs.utc(lastTableLoadDate).format('h:mm a')} UTC
            </Typography>
          )}
        </Box>
        <FiltersButton onClick={onFilterClick} filtersCount={selectedFiltersSize} />
      </Box>
      {/** Table of events */}
      {isLoading && (
        <Box sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress aria-label="Loading" />
        </Box>
      )}
      {!isLoading && (
        <WebhookEventTable
          webhooks={webhooks}
          events={events}
          sortQuery={sortQuery}
          isDataLoading={isDataLoading}
          pageData={pageData}
          setEventsPageNumber={setEventsPageNumber}
          setEventsPageSize={setEventsPageSize}
          setEventsSortQuery={setEventsSortQuery}
          retryWebhookEvent={retryWebhookEvent}
        />
      )}
      <FiltersPanel
        selectedFilters={selectedFilters}
        onClosePanel={onFilterClose}
        show={filtersPanelOpen}
        eventTypes={eventTypes}
        applyEventsFilters={applyEventsFilters}
      />
    </Box>
  );
};

export default EventLogsTab;
